/**
 * Everything the world is built out of, and everything in it worth pressing.
 *
 * One panel serving two tabs, because they are the same thing seen from two
 * sides. Scenery is what a place is made of and carries no menu; an object
 * carries a menu, and what those menu entries do is written elsewhere in the
 * content: a tree yields logs, an anvil makes bars into blades, a chest opens
 * the bank. Splitting them by whether they carry a menu is a split the data
 * already makes, so nothing has to be marked by hand and nothing can disagree.
 *
 * Renaming lives here as well, which is the thing that was missing: a first
 * tier of wood called the wrong thing could be placed a hundred times and never
 * corrected.
 */

import { useMemo, useState } from 'react';
import type { ItemDefinitionWire, ObjectDefinitionWire } from '@inverse/protocol';
import {
  SORT_LABELS,
  sortOf,
  actsOf,
  type Act,
  type ObjectLinks,
  type ObjectSort,
} from './objects';
import { ActsEditor } from './Acts';
import type { QuestWire } from '@inverse/protocol';
import type { AdminCommand } from '../Admin';
import { Explain } from '../Explain';

export function EditorObjects({
  /** True for the things that carry a menu, false for the things that do not. */
  interactive,
  objects,
  items,
  quests,
  characters,
  links,
  onCommand,
}: {
  interactive: boolean;
  objects: readonly ObjectDefinitionWire[];
  items: readonly ItemDefinitionWire[];
  /** For the conditions and effects that name a quest or somebody. */
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
  links: ObjectLinks;
  onCommand: AdminCommand;
}): JSX.Element {
  const [chosen, setChosen] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [making, setMaking] = useState(false);
  const [saying, setSaying] = useState('');

  /** What is being typed, kept apart from the world until it is written down. */
  const [draft, setDraft] = useState<{
    name: string;
    examine: string;
    actions: string;
    family: string;
    tier: string;
  } | null>(null);

  /** A new one borrows the shape of something that already has one. */
  const [drawnAs, setDrawnAs] = useState(0);

  const mine = useMemo(
    () => objects.filter((object) => object.actions.length > 0 === interactive),
    [interactive, objects],
  );

  const groups = useMemo(() => {
    const wanted = search.trim().toLowerCase();
    const matching = wanted
      ? mine.filter(
          (object) => object.name.toLowerCase().includes(wanted) || String(object.id) === wanted,
        )
      : mine;

    const sorts: ObjectSort[] = interactive ? ['harvest', 'craft', 'utility', 'other'] : ['other'];

    return sorts
      .map((sort) => ({
        sort,
        entries: matching
          .filter((object) => (interactive ? sortOf(object, links) === sort : true))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter((group) => group.entries.length > 0);
  }, [interactive, links, mine, search]);

  const held = objects.find((object) => object.id === chosen) ?? null;

  // What is on screen follows what is chosen, and is left alone while it is
  // being typed into: a form that filled itself in again every time the world
  // sent the definitions would be a form nobody could finish a word in.
  const [taken, setTaken] = useState<number | null>(null);
  if ((held?.id ?? null) !== taken) {
    setTaken(held?.id ?? null);
    setDraft(
      held
        ? {
            name: held.name,
            examine: held.examine,
            actions: held.actions.join(', '),
            family: held.family,
            tier: held.tier,
          }
        : null,
    );
  }

  /**
   * The acts on whatever is chosen, and how they are written down.
   *
   * Held rather than read straight off the definition, because the definitions
   * arrive again every time anything is saved and a list rebuilt underneath
   * somebody mid-edit is a list that loses what they were doing.
   */
  const [acts, setActs] = useState<Act[]>([]);
  const [tookActs, setTookActs] = useState<number | null>(null);
  if ((held?.id ?? null) !== tookActs) {
    setTookActs(held?.id ?? null);
    setActs(held ? actsOf(held) : []);
  }

  const writeActs = (wanted: Act[]): void => {
    setActs(wanted);
    if (!held) return;

    onCommand('setObject', { object: held.id }, { acts: JSON.stringify(wanted) });
    setSaying('Written down. Save the list to keep it.');
  };

  const write = (): void => {
    if (!held || !draft) return;

    onCommand(
      'setObject',
      { object: held.id },
      {
        name: draft.name,
        examine: draft.examine,
        actions: draft.actions,
        family: draft.family,
        tier: draft.tier,
      },
    );

    setSaying(`${draft.name || 'It'} written down. Save the list to keep it.`);
  };

  /** What the world does with this one, said in words rather than in ids. */
  const facts = (object: ObjectDefinitionWire): string[] => {
    const said: string[] = [];

    const yielded = links.yields
      .filter((one) => one.objectId === object.id)
      .map((one) => items.find((item) => item.id === one.itemId)?.name ?? `item ${one.itemId}`);
    if (yielded.length > 0) said.push(`Gives ${yielded.join(', ')}.`);

    const made = links.recipes
      .filter((one) => one.objectId === object.id)
      .map((one) => items.find((item) => item.id === one.outputId)?.name ?? `item ${one.outputId}`);
    if (made.length > 0) said.push(`Makes ${made.slice(0, 8).join(', ')}.`);

    if (object.actions.some((action) => action === 'Open' || action === 'Deposit')) {
      said.push('Opens the bank.');
    }

    if (object.dungeon) said.push(`Leads into ${object.dungeon}.`);
    if (object.exit) said.push('Leads back out.');
    if (object.looksLike > 0) {
      const shape = objects.find((one) => one.id === object.looksLike);
      said.push(`Drawn as ${shape?.name ?? `scenery ${object.looksLike}`}.`);
    }

    return said;
  };

  if (making) {
    return (
      <>
        <h3>{interactive ? 'A new object' : 'A new piece of scenery'}</h3>
        <p className="hint">
          It borrows the shape of something that already has one.
          <Explain>
            A new kind of thing is its own name, its own description and its own menu over a shape
            that already exists. Giving it a model of its own is a job for the Models tab.
          </Explain>
        </p>

        <label className="editor-field">
          <span>Name</span>
          <input
            type="text"
            value={draft?.name ?? ''}
            placeholder={interactive ? 'Pine' : 'Signpost'}
            onChange={(event) =>
              setDraft((was) => ({
                name: event.target.value,
                examine: was?.examine ?? '',
                actions: was?.actions ?? '',
                family: was?.family ?? '',
                tier: was?.tier ?? '',
              }))
            }
          />
        </label>

        <label className="editor-field">
          <span>Description</span>
          <input
            type="text"
            value={draft?.examine ?? ''}
            placeholder="Tall, and full of sap."
            onChange={(event) =>
              setDraft((was) => (was ? { ...was, examine: event.target.value } : null))
            }
          />
        </label>

        {interactive ? (
          <label className="editor-field">
            <span>Menu</span>
            <input
              type="text"
              value={draft?.actions ?? ''}
              placeholder="Chop, Search"
              onChange={(event) =>
                setDraft((was) => (was ? { ...was, actions: event.target.value } : null))
              }
            />
          </label>
        ) : null}

        <label className="editor-field">
          <span>Shape</span>
          <select value={drawnAs} onChange={(event) => setDrawnAs(Number(event.target.value))}>
            <option value={0}>Choose something it looks like</option>
            {objects.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>

        {saying ? <p className="hint warning">{saying}</p> : null}

        <div className="editor-brushes">
          <button
            type="button"
            className="editor-tab"
            disabled={(draft?.name ?? '').trim().length === 0 || drawnAs === 0}
            onClick={() => {
              onCommand(
                'makeObject',
                { from: drawnAs },
                {
                  name: draft?.name ?? '',
                  examine: draft?.examine ?? '',
                  actions: draft?.actions ?? '',
                },
              );

              setMaking(false);
              setDraft(null);
              setSaying('Made. It is in the list, and the list needs saving to keep it.');
            }}
          >
            Make It
          </button>
          <button type="button" className="editor-tab" onClick={() => setMaking(false)}>
            Back
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="editor-brushes">
        <button
          type="button"
          className="editor-tab"
          onClick={() => {
            setMaking(true);
            setDraft({ name: '', examine: '', actions: '', family: '', tier: '' });
            setSaying('');
          }}
        >
          {interactive ? 'New Object' : 'New Scenery'}
        </button>
        <button type="button" className="editor-tab" onClick={() => onCommand('saveObjects')}>
          Save The List
        </button>
      </div>

      <input
        className="editor-search"
        type="text"
        placeholder="Search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <div className="editor-list">
        {groups.map((group) => (
          <div key={group.sort}>
            {interactive ? (
              <h4 className="editor-group-head">
                {SORT_LABELS[group.sort]}
                <span>{group.entries.length}</span>
              </h4>
            ) : null}

            {group.entries.map((object) => (
              <button
                key={object.id}
                type="button"
                className={object.id === chosen ? 'editor-row on' : 'editor-row'}
                onClick={() => {
                  setChosen(object.id);
                  setSaying('');
                }}
              >
                <span>{object.name}</span>
                <span className="editor-row-id">{object.id}</span>
              </button>
            ))}
          </div>
        ))}

        {groups.length === 0 ? <p className="hint">Nothing by that name.</p> : null}
      </div>

      {held && draft ? (
        <>
          <h3>{held.name}</h3>

          <label className="editor-field">
            <span>Name</span>
            <input
              type="text"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              onBlur={write}
              onKeyDown={(event) => {
                // Enter means the same as leaving the box. Typing a name and
                // pressing Enter is what everybody does, and a form that only
                // wrote down what was typed when the pointer went elsewhere is
                // a form that quietly loses the last thing typed into it.
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
          </label>

          <label className="editor-field">
            <span>Description</span>
            <input
              type="text"
              value={draft.examine}
              onChange={(event) => setDraft({ ...draft, examine: event.target.value })}
              onBlur={write}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
          </label>

          <label className="editor-field">
            <span>Menu</span>
            <input
              type="text"
              value={draft.actions}
              placeholder="Chop, Search"
              onChange={(event) => setDraft({ ...draft, actions: event.target.value })}
              onBlur={write}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
          </label>

          {/* What kind of thing it is one of, and which one. Two words that turn
              a new tier into another rung of something the browser already
              offers rather than a stranger beside it. */}
          <label className="editor-field">
            <span>Family</span>
            <input
              type="text"
              value={draft.family}
              placeholder="tree"
              onChange={(event) => setDraft({ ...draft, family: event.target.value })}
              onBlur={write}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
          </label>

          <label className="editor-field">
            <span>Tier</span>
            <input
              type="text"
              value={draft.tier}
              placeholder="pine"
              onChange={(event) => setDraft({ ...draft, tier: event.target.value })}
              onBlur={write}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
          </label>

          {/* Whether an arrow or a spell can pass it. Tall things stop a shot;
              things low to the ground, or open like bars and fences, do not. */}
          <label className="editor-field">
            <span>Blocks shots</span>
            <input
              type="checkbox"
              checked={held.blocksSight}
              onChange={(event) => {
                onCommand(
                  'setObject',
                  { object: held.id },
                  { blocksSight: event.target.checked ? 'yes' : 'no' },
                );
                setSaying(
                  event.target.checked
                    ? `${held.name} now stops arrows and spells. Save the list to keep it.`
                    : `${held.name} can now be shot over. Save the list to keep it.`,
                );
              }}
            />
          </label>

          <StandsFields
            key={held.id}
            held={held}
            objects={objects}
            onSet={(numbers, text, said) => {
              onCommand('setObject', { object: held.id, ...numbers }, text);
              setSaying(`${said} Save the list to keep it.`);
            }}
          />

          {/* What each menu entry does, written here and run by the world. The
              same builder a character uses, because an act is an act wherever
              it hangs. */}
          <ActsEditor
            acts={acts}
            menu={draft.actions
              .split(',')
              .map((one) => one.trim())
              .filter((one) => one.length > 0)}
            items={items}
            quests={quests}
            characters={characters}
            objects={objects}
            shops={links.shops}
            onChange={writeActs}
          />

          {/* What the world already does with it, so somebody renaming a thing
              can see what they are renaming. Read here and written where it
              belongs: what a tree gives is a fact about gathering, and a panel
              that let it be typed in two places would let the two disagree. */}
          <h4>What else it does</h4>
          {facts(held).length > 0 ? (
            <ul className="editor-facts-list">
              {facts(held).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : (
            <p className="hint">
              Nothing yet.
              {interactive
                ? ' What its menu does is written where that thing lives: what it gives on the spot itself, and what is made at it in the item that is made.'
                : ' Scenery is what a place is made of rather than something to press.'}
            </p>
          )}

          {saying ? <p className="hint">{saying}</p> : null}
        </>
      ) : (
        <p className="hint">Choose one to rename it or change what its menu says.</p>
      )}
    </>
  );
}

/**
 * How a thing stands: what it stops, how much ground it takes, which of a
 * tile's layers it is, and whether it is a door or a stair.
 *
 * Changed here, it changes on every one already standing in the world, and
 * the ground round them is sent again, so nobody walks through something the
 * world now says is solid.
 */
function StandsFields({
  held,
  objects,
  onSet,
}: {
  held: ObjectDefinitionWire;
  objects: readonly ObjectDefinitionWire[];
  onSet: (numbers: Record<string, number>, text: Record<string, string>, said: string) => void;
}): JSX.Element {
  const [width, setWidth] = useState(String(held.width));
  const [height, setHeight] = useState(String(held.height));
  const [swings, setSwings] = useState(String(held.swingsBack));
  const size = (): void => {
    const across = Math.min(8, Math.max(1, Math.round(Number(width) || 1)));
    const along = Math.min(8, Math.max(1, Math.round(Number(height) || 1)));
    if (across === held.width && along === held.height) return;
    onSet({ width: across, height: along }, {}, `${held.name} now takes ${across} by ${along}.`);
  };

  return (
    <>
      <h4>How it stands</h4>
      <label className="editor-field">
        <span>Stops people</span>
        <select
          value={held.barrier}
          onChange={(event) =>
            onSet({}, { barrier: event.target.value }, `${held.name} stops people differently now.`)
          }
        >
          <option value="solid">On all the ground it covers</option>
          <option value="wall">Along one edge, like a wall or a fence</option>
          <option value="open">Not at all</option>
        </select>
      </label>
      <div className="editor-brushes">
        <label className="editor-small">
          <span>Across</span>
          <input
            type="number"
            min={1}
            max={8}
            value={width}
            onChange={(event) => setWidth(event.target.value)}
            onBlur={size}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
          />
        </label>
        <label className="editor-small">
          <span>Along</span>
          <input
            type="number"
            min={1}
            max={8}
            value={height}
            onChange={(event) => setHeight(event.target.value)}
            onBlur={size}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
          />
        </label>
      </div>
      <label className="editor-field">
        <span>Layer</span>
        <select
          value={held.layer}
          onChange={(event) =>
            onSet({}, { layer: event.target.value }, `${held.name} is on another layer now.`)
          }
        >
          <option value="standing">Stands on the tile</option>
          <option value="floor">Laid on the tile</option>
          <option value="wall">On an edge of the tile</option>
          <option value="roof">Over the tile</option>
        </select>
      </label>
      <p className="hint">
        A tile holds one thing on each layer: a chest put down on boards replaces what stood there,
        not the boards.
      </p>
      {(
        [
          ['floor', 'A floor, or boards over water', held.floor],
          ['roof', 'A roof, hidden while somebody is under it', held.roof],
          ['afloat', 'Part of the water, like a fishing spot', held.afloat],
        ] as const
      ).map(([field, label, on]) => (
        <label className="editor-field" key={field}>
          <span>{label}</span>
          <input
            type="checkbox"
            checked={on}
            onChange={(event) =>
              onSet({}, { [field]: event.target.checked ? 'yes' : 'no' }, `${held.name} changed.`)
            }
          />
        </label>
      ))}
      <label className="editor-field">
        <span>Stairs</span>
        <select
          value={held.climb}
          onChange={(event) => onSet({}, { climb: event.target.value }, `${held.name} changed.`)}
        >
          <option value="">Not a way up or down</option>
          <option value="up">Up to the floor above</option>
          <option value="down">Down to the floor below</option>
        </select>
      </label>
      <label className="editor-field">
        <span>Opens into</span>
        <select
          value={held.opensTo}
          onChange={(event) =>
            onSet(
              { opensTo: Number(event.target.value) },
              {},
              Number(event.target.value) > 0
                ? `${held.name} now opens and shuts.`
                : `${held.name} no longer opens.`,
            )
          }
        >
          <option value={0}>Does not open</option>
          {objects
            .filter((one) => one.id !== held.id)
            .map((one) => (
              <option key={one.id} value={one.id}>
                {one.name} ({one.id})
              </option>
            ))}
        </select>
      </label>
      {held.opensTo > 0 ? (
        <label className="editor-small">
          <span>Swings back after (ticks)</span>
          <input
            type="number"
            min={0}
            max={36000}
            value={swings}
            onChange={(event) => setSwings(event.target.value)}
            onBlur={() => {
              const ticks = Math.max(0, Math.round(Number(swings) || 0));
              if (ticks !== held.swingsBack) {
                onSet({ swingsBack: ticks }, {}, `${held.name} swings back after ${ticks} ticks.`);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
          />
        </label>
      ) : null}
    </>
  );
}
